import camelCase from 'camelcase'

import { sendToSentry } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'

import { QuestionFlowType } from './types'
import { getLocalState } from './utils'

export const validate = values => {
  const errors = {}

  // Annual Income, Combined Annual Income, and Self Employed Income are
  // required
  const requiredFields = ['annual_income', 'combined_annual_income', 'self_employed_income']
  requiredFields.forEach(field => {
    if (typeof values[field] !== 'undefined' && values[field] === '') {
      errors[field] = 'This income field is required'
    }
  })

  if ((values.annual_income || values.combined_annual_income) && values.self_employed_income) {
    const annualIncome =
      parseInt(values.annual_income, 10) || parseInt(values.combined_annual_income, 10)
    const selfEmployedIncome = parseInt(values.self_employed_income, 10)

    if (selfEmployedIncome > annualIncome) {
      errors.self_employed_income =
        'Self-Employed income must be less than or equal to Annual Income'
    }
  }

  return errors
}

export const mapPropsToValues = ({ client, rows }) => {
  const propValues = {}

  // Query for existing StoredQuestion items
  const { storedQuestions } = getLocalState(client)

  if (rows && rows.length > 0 && rows[0]) {
    rows.map(row => {
      row.map(field => {
        // Fetch value of question from local storage, if it exists
        const questionId = field.name
        let answerId = ''

        const questionIndex = storedQuestions.findIndex(
          storedQuestion => storedQuestion.questionId === questionId,
        )

        if (questionIndex !== -1) {
          answerId = storedQuestions[questionIndex].answerId
        }

        propValues[questionId] = answerId
      })
    })
  }

  return propValues
}

export const handleFormSubmit = (values, { props, ...ownProps }) => {
  ownProps.setSubmitting(true)

  const { storedQuestions, coreSuitabilityQuestions, existingCoreCustomer } = getLocalState(
    props.client,
  )
  if (existingCoreCustomer) {
    return props.route({ from: FlowRoute.LaterSuitabilityQuestions, action: 'continue' })
  }
  const laterStoredQuestions = storedQuestions.filter(
    storedQuestion => storedQuestion.flowType === QuestionFlowType.Later,
  )
  const coreStoredQuestions = storedQuestions.filter(
    storedQuestion => storedQuestion.flowType === QuestionFlowType.Core,
  )
  let questionId = null
  let answerId = null

  // Later Suitability
  // Define Later Suitability fields that are numeric in type
  const incomeFields = ['annualIncome', 'combinedAnnualIncome', 'selfEmployedIncome']
  const laterPayload = {}

  laterStoredQuestions.forEach(storedQuestion => {
    const key = camelCase(storedQuestion.questionId)

    // Income field values get converted to integer type
    const value = incomeFields.includes(key)
      ? parseInt(storedQuestion.answerId, 10)
      : camelCase(storedQuestion.answerId)

    laterPayload[key] = value
  })

  // Core Suitability
  const suitabilityPayload = []

  coreStoredQuestions.forEach(storedQuestion => {
    const key = storedQuestion.questionId
    const value = storedQuestion.answerId
    suitabilityPayload.push({
      questionId: key,
      answerId: value,
    })
  })

  // Add Employment Status
  // Find the questionId and answerId for Core Suitability Employment Status question
  // based on customer's answer for Later Financial Employment Status question
  const employmentCoreSuitabilityQuestion = coreSuitabilityQuestions.find(
    ({ question }) => question.key === 'employment',
  ).question

  const employmentLaterFinancialQuestion = laterStoredQuestions.find(
    storedQuestion => storedQuestion.questionId === 'employment_status',
  )
  questionId = employmentCoreSuitabilityQuestion.id

  // Find the answerId of the Employment Status question in Core Suitability
  answerId = employmentCoreSuitabilityQuestion.possibleAnswers.find(
    answer => answer.key === employmentLaterFinancialQuestion.answerId,
  ).id

  suitabilityPayload.push({
    questionId,
    answerId,
  })

  // Add Yearly Income
  // Find the questionId and answerId for Core Suitability Yearly Income question
  // based on customer's answer for Later Financial Annual Income question
  const yearlyIncomeCoreSuitabilityQuestion = coreSuitabilityQuestions.find(
    ({ question }) => question.key === 'yearly_income',
  ).question

  const annualIncomeLaterFinancialQuestion = laterStoredQuestions.find(storedQuestion =>
    ['annual_income', 'combined_annual_income'].includes(storedQuestion.questionId),
  )
  questionId = yearlyIncomeCoreSuitabilityQuestion.id

  // Translate answer into an income range
  const incomeRanges = [
    [250001, 'd39cd6f0-5b75-44b5-84fc-d57840cfc6fd'],
    [100001, '5b7ad5a0-a904-48b8-bacc-e91dc92c668a'],
    [50001, 'a2b20462-cfc3-4aa3-8027-c631e1baa97f'],
    [10001, '46bcd862-0b37-485a-9fd4-849fb6699437'],
    [0, '30bb6e27-4c5d-4d28-bd8f-fda433f9e041'],
  ]

  const annualIncome = parseInt(annualIncomeLaterFinancialQuestion.answerId, 10)
  answerId = incomeRanges.find(range => annualIncome >= range[0])[1]

  suitabilityPayload.push({
    questionId,
    answerId,
  })

  const updateProfilePromise = props.updateProfile({
    variables: {
      input: laterPayload,
    },
  })

  const updateSuitabilityPromise = props
    .updateSuitability({
      variables: {
        input: suitabilityPayload,
      },
    })
    .then(({ data: { updateSuitability } }) => {
      const portfolioId = updateSuitability.recommendedPortfolio.id
      return props.setPortfolio({ variables: { input: { id: portfolioId } } })
    })

  return Promise.all([updateProfilePromise, updateSuitabilityPromise])
    .then(() => {
      props.route({ from: FlowRoute.LaterSuitabilityQuestions, action: 'continue' })
    })
    .catch(error => {
      ownProps.setSubmitting(false)
      sendToSentry(error)
    })
}
